import { c as _c } from "react/compiler-runtime";
import { SubscriptionPriceBoxNoticeData } from '@udemy-v2/notices';
import { ConsumerSubscriptionPlan } from '@udemy-v2/subscription-graphql';
import { useFormatCurrency } from '@udemy-v2/react-currency-formatting';

/**
 * Enum containing all the possible field names that could be interpolated into a given field for a subscription
 * discount notice.
 */
export enum NoticeInterpolation {
  SUBS_ANNUAL_OFFER_DISCOUNT_PRICE = 'subs_annual_offer_discount_price',
  SUBS_ANNUAL_OFFER_MONTHLY_DISCOUNT_PRICE = 'subs_annual_offer_monthly_discount_price',
  SUBS_ANNUAL_OFFER_MONTHLY_NON_DISCOUNT_PRICE = 'subs_annual_offer_monthly_non_discount_price',
  SUBS_MONTHLY_OFFER_DISCOUNT_PRICE = 'subs_monthly_offer_discount_price',
  SUBS_ANNUAL_OFFER_DISCOUNT_PERCENT = 'subs_annual_offer_discount_percent',
  SUBS_MONTHLY_OFFER_DISCOUNT_PERCENT = 'subs_monthly_offer_discount_percent',
  SUBS_ANNUAL_OFFER_DISCOUNT_DURATION = 'subs_annual_offer_discount_duration',
  SUBS_MONTHLY_OFFER_DISCOUNT_DURATION = 'subs_monthly_offer_discount_duration',
  SUBS_ANNUAL_OFFER_NON_DISCOUNT_PRICE = 'subs_annual_offer_non_discount_price',
  SUBS_MONTHLY_OFFER_NON_DISCOUNT_PRICE = 'subs_monthly_offer_non_discount_price',
}

/**
 * Gets the correctly formatted HTML target value for an anchor tag from the notice target which is either 'self' or 'new_tab'
 * @param noticeTarget
 */
export const getHTMLTarget = (noticeTarget: string) => {
  return noticeTarget === 'self' ? '_self' : '_blank';
};

/**
 * React hook that interpolates subscription price box notice data with values from ConsumerSubscriptionPlan
 * @param consumerSubscriptionPlan The subscription plan containing pricing information, can be undefined
 * @param notice The notice template to interpolate with pricing information, can be undefined
 * @returns The interpolated notice data or undefined if interpolation fails
 */
export const useInterpolatedSubscriptionNotice = (consumerSubscriptionPlan, notice) => {
  const $ = _c(26);
  const formatCurrencyFn = useFormatCurrency();
  if (!consumerSubscriptionPlan || !notice) {
    return;
  }
  let t0;
  if ($[0] !== formatCurrencyFn) {
    t0 = price => formatCurrencyFn.formatCurrency(price);
    $[0] = formatCurrencyFn;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const formatCurrency = t0;
  let t1;
  if ($[2] !== consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountInterval?.duration || $[3] !== consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountMonthlyPrice || $[4] !== consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPercent || $[5] !== consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPrice || $[6] !== consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountMonthlyPrice || $[7] !== consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountPrice || $[8] !== consumerSubscriptionPlan.annualPlanOption?.listPrice || $[9] !== consumerSubscriptionPlan.annualPlanOption?.monthlyPrice || $[10] !== consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountInterval?.duration || $[11] !== consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPercent || $[12] !== consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPrice || $[13] !== consumerSubscriptionPlan.monthlyDiscount?.subscriptionNonDiscountPrice || $[14] !== consumerSubscriptionPlan.monthlyPlanOption || $[15] !== formatCurrency) {
    const extractInterpolationKeysMap = () => {
      const formatCurrencyField = price_0 => price_0 ? formatCurrency(price_0) : undefined;
      return {
        [NoticeInterpolation.SUBS_ANNUAL_OFFER_DISCOUNT_PRICE]: formatCurrencyField(consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPrice),
        [NoticeInterpolation.SUBS_ANNUAL_OFFER_MONTHLY_DISCOUNT_PRICE]: formatCurrencyField(consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountMonthlyPrice),
        [NoticeInterpolation.SUBS_ANNUAL_OFFER_MONTHLY_NON_DISCOUNT_PRICE]: formatCurrencyField(consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountMonthlyPrice ?? consumerSubscriptionPlan?.annualPlanOption?.monthlyPrice.amount),
        [NoticeInterpolation.SUBS_MONTHLY_OFFER_DISCOUNT_PRICE]: formatCurrencyField(consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPrice),
        [NoticeInterpolation.SUBS_ANNUAL_OFFER_DISCOUNT_PERCENT]: consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPercent,
        [NoticeInterpolation.SUBS_MONTHLY_OFFER_DISCOUNT_PERCENT]: consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPercent,
        [NoticeInterpolation.SUBS_ANNUAL_OFFER_DISCOUNT_DURATION]: consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountInterval?.duration,
        [NoticeInterpolation.SUBS_MONTHLY_OFFER_DISCOUNT_DURATION]: consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountInterval?.duration,
        [NoticeInterpolation.SUBS_ANNUAL_OFFER_NON_DISCOUNT_PRICE]: formatCurrencyField(consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountPrice ?? consumerSubscriptionPlan.annualPlanOption?.listPrice.amount),
        [NoticeInterpolation.SUBS_MONTHLY_OFFER_NON_DISCOUNT_PRICE]: formatCurrencyField(consumerSubscriptionPlan.monthlyDiscount?.subscriptionNonDiscountPrice ?? consumerSubscriptionPlan.monthlyPlanOption.listPrice.amount)
      };
    };
    const extractedSubscriptionValues = extractInterpolationKeysMap();
    t1 = template => template.replace(/%\(\w+\)s/g, function (e) {
      return String((extractedSubscriptionValues as any)[e.slice(2, -2)]);
    });
    $[2] = consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountInterval?.duration;
    $[3] = consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountMonthlyPrice;
    $[4] = consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPercent;
    $[5] = consumerSubscriptionPlan.annualDiscount?.subscriptionDiscountPrice;
    $[6] = consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountMonthlyPrice;
    $[7] = consumerSubscriptionPlan.annualDiscount?.subscriptionNonDiscountPrice;
    $[8] = consumerSubscriptionPlan.annualPlanOption?.listPrice;
    $[9] = consumerSubscriptionPlan.annualPlanOption?.monthlyPrice;
    $[10] = consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountInterval?.duration;
    $[11] = consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPercent;
    $[12] = consumerSubscriptionPlan.monthlyDiscount?.subscriptionDiscountPrice;
    $[13] = consumerSubscriptionPlan.monthlyDiscount?.subscriptionNonDiscountPrice;
    $[14] = consumerSubscriptionPlan.monthlyPlanOption;
    $[15] = formatCurrency;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  const interpolateSubscriptionDiscountKeys = t1;
  const checkNoticeFieldsForUndefinedOrNull = _temp2;
  const {
    title,
    subtitle,
    subtitle_link_text,
    cta_1_text,
    cta_1_target,
    tagline
  } = notice;
  let t2;
  if ($[17] !== cta_1_target || $[18] !== cta_1_text || $[19] !== interpolateSubscriptionDiscountKeys || $[20] !== notice || $[21] !== subtitle || $[22] !== subtitle_link_text || $[23] !== tagline || $[24] !== title) {
    const interpolatedPriceBoxNotice = {
      ...notice,
      title: interpolateSubscriptionDiscountKeys(title),
      subtitle: interpolateSubscriptionDiscountKeys(subtitle),
      subtitle_link_text: interpolateSubscriptionDiscountKeys(subtitle_link_text),
      cta_1_text: interpolateSubscriptionDiscountKeys(cta_1_text),
      cta_1_target: getHTMLTarget(cta_1_target),
      tagline: interpolateSubscriptionDiscountKeys(tagline)
    };
    t2 = !checkNoticeFieldsForUndefinedOrNull(interpolatedPriceBoxNotice) ? interpolatedPriceBoxNotice : undefined;
    $[17] = cta_1_target;
    $[18] = cta_1_text;
    $[19] = interpolateSubscriptionDiscountKeys;
    $[20] = notice;
    $[21] = subtitle;
    $[22] = subtitle_link_text;
    $[23] = tagline;
    $[24] = title;
    $[25] = t2;
  } else {
    t2 = $[25];
  }
  return t2;
};
function _temp(field) {
  if (typeof field === "string") {
    return field.includes("undefined") || field.includes("null");
  }
  return false;
}
function _temp2(subscriptionNotice) {
  return Object.values(subscriptionNotice).find(_temp);
}